import { posthog } from '@/utils/tracking/posthog';

const STORAGE_KEY = 'vela_attribution';

const QUERY_PARAMS = [
  'keyword',
  'mt',
];

/**
 * Retrieves stored attribution data from localStorage.
 *
 * @returns {Object} Attribution object with first_touch, last_touch, and all_touches
 */
export function getAttribution() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
  } catch (error) {
    return {};
  }
}

/**
 * Saves attribution data to localStorage.
 *
 * @param {Object | undefined} value - attribution data to save or undefined to delete
 */
export function setAttribution(value) {
  try {
    if (value === undefined) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    }
  } catch (error) {
    console.error(error);
  }
}

/**
 * Ad platform click ID parameters.
 * Each platform appends its own click ID to URLs for conversion tracking.
 */
const CLICK_ID_PARAMS = {
  gclid: 'google',      // Google Ads
  gbraid: 'google',     // Google Ads (iOS app-to-web)
  wbraid: 'google',     // Google Ads (iOS web)
  fbclid: 'meta',       // Meta (Facebook/Instagram)
  msclkid: 'microsoft', // Microsoft/Bing Ads
  li_fat_id: 'linkedin', // LinkedIn Ads
  ttclid: 'tiktok',     // TikTok Ads
  twclid: 'twitter',    // Twitter/X Ads
  rdt_cid: 'reddit',    // Reddit Ads
};

/**
 * Detects which ad platform a user arrived from by checking URL for click IDs.
 *
 * @param {URLSearchParams} params - URL search params to check
 * @returns {{ param: string, value: string, platform: string } | null} Click ID info or null if none found
 */
function detectAdPlatform(params) {
  for (const [param, platform] of Object.entries(CLICK_ID_PARAMS)) {
    const value = params.get(param);
    if (value) {
      return { param, value, platform };
    }
  }
  return null;
}

const UTM_KEY_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

/** Meta Pixel first-party cookie; used for CAPI matching. Undefined if Pixel not installed. */
export function getFbpFromCookie() {
  if (typeof document === 'undefined' || !document.cookie) return undefined;
  const match = document.cookie.match(/_fbp=([^;]+)/);
  return match ? match[1].trim() : undefined;
}

/**
 * Returns a stable key for comparing touches. Same key = same touch for deduplication.
 * - With click ID: key is the click ID value (UTM is fixed for that click).
 * - Without click ID: key is all UTM params joined so organic campaigns are distinct.
 * Only append to all_touches when this changes.
 */
function getTouchKey(touch) {
  const clickIdParam = Object.keys(CLICK_ID_PARAMS).find((param) => touch[param]);
  const clickIdValue = clickIdParam ? touch[clickIdParam] : null;
  if (clickIdValue) return String(clickIdValue);
  return UTM_KEY_FIELDS.map((p) => touch[p] ?? '').join('|');
}

/**
 * Builds PostHog person property objects from stored attribution.
 * set = last touch (plain keys) — sent as $set, updates on each capture.
 * setOnce = first touch ($initial_*) — sent as $set_once, only set once by PostHog.
 * Use with setPersonProperties(set, setOnce).
 *
 * @param {Object} stored - Attribution from getAttribution(): { first_touch, last_touch }
 * @returns {{ set: Object, setOnce: Object }}
 */
export function getPosthogAttributionProperties(stored) {
  const setOnce = {};
  const set = {};
  const ft = stored?.first_touch;
  const lt = stored?.last_touch;

  if (ft) {
    const clickIdParam = Object.keys(CLICK_ID_PARAMS).find((param) => ft[param]);
    Object.assign(setOnce, {
      ...(clickIdParam && { [`$initial_${clickIdParam}`]: ft[clickIdParam] }),
      $initial_ad_platform: ft.ad_platform,
      $initial_utm_source: ft.utm_source,
      $initial_utm_medium: ft.utm_medium,
      $initial_utm_campaign: ft.utm_campaign,
      $initial_utm_content: ft.utm_content,
      $initial_utm_term: ft.utm_term,
      $initial_referrer: ft.referrer,
      $initial_pathname: ft.pathname,
    });
  }
  if (lt) {
    const clickIdParam = Object.keys(CLICK_ID_PARAMS).find((param) => lt[param]);
    Object.assign(set, {
      ...(clickIdParam && { [clickIdParam]: lt[clickIdParam] }),
      ad_platform: lt.ad_platform,
      utm_source: lt.utm_source,
      utm_medium: lt.utm_medium,
      utm_campaign: lt.utm_campaign,
      utm_content: lt.utm_content,
      utm_term: lt.utm_term,
      $referrer: lt.referrer,
      $pathname: lt.pathname,
    });
  }
  return { set, setOnce };
}

/**
 * Captures attribution data (click IDs, UTM params) from URL and stores in localStorage.
 * Sets PostHog person properties on the anonymous user: $initial_* (first touch, $set_once)
 * and last-touch props (plain keys, $set). Call on page load after posthog.init().
 *
 * Supports multiple ad platforms: Google (gclid, wbraid, gbraid), Meta, Microsoft, LinkedIn, TikTok, Twitter, Reddit.
 */
export function captureAttribution() {
  const params = new URLSearchParams(window.location.search);
  const clickId = detectAdPlatform(params);

  // Only capture if there's campaign attribution data (click ID or UTM source)
  // Referrer alone isn't actionable for CAC/ad attribution
  if (!clickId && !params.get('utm_source')) return;

  const touch = {
    // Context
    pathname: window.location.pathname,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),

    // UTM parameters (platform-agnostic)
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),

    // Meta Pixel browser ID (when Pixel is on this origin); improves CAPI attribution
    ...(getFbpFromCookie() && { fbp: getFbpFromCookie() }),
  };

  // Click ID (platform-specific) - stored with its original param name (gclid, fbclid, etc.)
  if (clickId) {
    touch[clickId.param] = clickId.value;
    touch.ad_platform = clickId.platform;
  }

  for (const param of QUERY_PARAMS) {
    const value = params.get(param);

    if (value) {
      touch[param] = value;
    }
  }

  const stored = getAttribution();
  const posthogReady = posthog.initialized && posthog.client?.setPersonProperties;

  // function getPosthogSkipReason() {
  //   if (posthog.initialized === undefined || posthog.initialized === false) {
  //     return 'PostHog not initialized (VELA_POSTHOG_KEY or VELA_POSTHOG_HOST missing, or init() not run)';
  //   }
  //   if (!posthog.client) {
  //     return 'PostHog client not available';
  //   }
  //   if (typeof posthog.client.setPersonProperties !== 'function') {
  //     return 'PostHog setPersonProperties not available';
  //   }
  //   return null;
  // }

  // First touch: only set once in localStorage (never overwrite)
  if (!stored.first_touch) {
    stored.first_touch = touch;
  }

  // Last touch: always update (we only reach here if we have campaign data)
  stored.last_touch = touch;

  // Set person properties on anonymous user only. $set = last touch (plain keys), $set_once = first touch ($initial_*).
  if (posthogReady) {
    const { set, setOnce } = getPosthogAttributionProperties(stored);
    posthog.client.setPersonProperties(set, setOnce);
  }

  // All touches: append only when click ID and/or utm_source differ from last touch
  if (!stored.all_touches) {
    stored.all_touches = [];
  }

  const lastTouch = stored.all_touches[stored.all_touches.length - 1];
  const isDifferentAttribution = !lastTouch || getTouchKey(touch) !== getTouchKey(lastTouch);

  if (isDifferentAttribution) {
    stored.all_touches.push(touch);
  }

  setAttribution(stored);
}
